import { NumberInput } from '@homestake/shared/ui';
import { use$, useValue } from '@legendapp/state/react';
import {
  Button,
  InputLabel,
  SegmentedControl,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  AutoBetConfigurationSchema,
  AutoBetOnWinLossAction,
  autoBetState$,
  configurationState$,
  DEFAULT_AUTO_BET_CONFIGURATION,
} from '../../../state';
import { freeBetState$ } from '../../../state/free-bet.state';
import { showSuccessNotification } from '../../../utils';

export const ConfigureAutoBetModalContent = () => {
  const isAutoBetting = use$(autoBetState$.isAutoBetting);
  const isFreeBetting = useValue(freeBetState$.isActive);
  const currency = use$(configurationState$.currency);
  const { t } = useTranslation();

  const { Field, reset, handleSubmit, Subscribe } = useForm({
    validators: {
      onChange: AutoBetConfigurationSchema,
    },
    defaultValues: {
      onWinAction: autoBetState$.onWinAction.peek(),
      onWinIncreaseValue: autoBetState$.onWinIncreaseValue.peek(),
      onLossAction: autoBetState$.onLossAction.peek(),
      onLossIncreaseValue: autoBetState$.onLossIncreaseValue.peek(),
      stopOnProfitValue: autoBetState$.stopOnProfitValue.peek(),
      stopOnLossValue: autoBetState$.stopOnLossValue.peek(),
    } as z.infer<typeof AutoBetConfigurationSchema>,
    onSubmit: ({ value }) => {
      autoBetState$.saveAutoBetConfiguration(value);
      showSuccessNotification({
        title: t('notifications.autoBetConfigSavedTitle'),
        message: t('notifications.autoBetConfigSavedMessage'),
        dataTestId: 'autobet-config-saved',
      });
    },
  });

  const handleResetAll = () => {
    reset(DEFAULT_AUTO_BET_CONFIGURATION);
  };

  return (
    <form
      data-test-id="configure-auto-bet-form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
      }}
    >
      <Stack data-test-id="configure-auto-bet-form-content" gap={12}>
        <Stack data-test-id="on-win-section" gap={0}>
          <InputLabel data-test-id={'on-win-label'} fw={500} size="xs">
            {t('configureAutoBetModal.onWinLabel')}
          </InputLabel>
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <Field name="onWinAction">
              {({ handleChange, state: { value } }) => (
                <SegmentedControl
                  data={[
                    {
                      value: AutoBetOnWinLossAction.RESET,
                      label: t('configureAutoBetModal.resetLabel'),
                    },
                    {
                      value: AutoBetOnWinLossAction.INCREASE_OR_DECREASE,
                      label: t('configureAutoBetModal.increaseByLabel'),
                    },
                  ]}
                  /* 		disabled={isAutoBetting} */
                  data-test-id="on-win-action-select"
                  disabled={isFreeBetting}
                  onChange={(newValue) => {
                    handleChange(newValue as AutoBetOnWinLossAction);
                  }}
                  value={value}
                />
              )}
            </Field>

            <Subscribe selector={(state) => state.values.onWinAction}>
              {(onWinAction) => (
                <Field name="onWinIncreaseValue">
                  {({
                    handleChange,
                    handleBlur,
                    state: {
                      value,
                      meta: { errors },
                    },
                    name,
                  }) => (
                    <NumberInput
                      data-test-id="on-win-increase-input"
                      decimalScale={2}
                      disabled={onWinAction === 'RESET' || isAutoBetting}
                      error={errors[0]?.message}
                      fixedDecimalScale
                      id={name}
                      name={name}
                      onBlur={handleBlur}
                      onChange={(newValue) => handleChange(Number(newValue))}
                      placeholder="0,00"
                      suffix=" %"
                      value={value}
                    />
                  )}
                </Field>
              )}
            </Subscribe>
          </SimpleGrid>
        </Stack>

        <Stack data-test-id="on-loss-section" gap={0}>
          <InputLabel data-test-id={'on-loss-label'} fw={500} size="xs">
            {t('configureAutoBetModal.onLossLabel')}
          </InputLabel>
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <Field name="onLossAction">
              {({ handleChange, state: { value } }) => (
                <SegmentedControl
                  data={[
                    {
                      value: AutoBetOnWinLossAction.RESET,
                      label: t('configureAutoBetModal.resetLabel'),
                    },
                    {
                      value: AutoBetOnWinLossAction.INCREASE_OR_DECREASE,
                      label: t('configureAutoBetModal.increaseByLabel'),
                    },
                  ]}
                  data-test-id="on-loss-action-select"
                  disabled={isFreeBetting}
                  onChange={(newValue) =>
                    handleChange(newValue as AutoBetOnWinLossAction)
                  }
                  value={value}
                />
              )}
            </Field>

            <Subscribe selector={(state) => state.values.onLossAction}>
              {(onLossAction) => (
                <Field name="onLossIncreaseValue">
                  {({
                    handleChange,
                    handleBlur,
                    state: {
                      value,
                      meta: { errors },
                    },
                    name,
                  }) => (
                    <NumberInput
                      data-test-id="on-loss-increase-input"
                      decimalScale={2}
                      disabled={onLossAction === 'RESET' || isAutoBetting}
                      error={errors[0]?.message}
                      fixedDecimalScale
                      id={name}
                      name={name}
                      onBlur={handleBlur}
                      onChange={(newValue) =>
                        handleChange(Number(newValue) || 0)
                      }
                      placeholder="0,00"
                      suffix=" %"
                      value={value}
                    />
                  )}
                </Field>
              )}
            </Subscribe>
          </SimpleGrid>
        </Stack>

        <Field name="stopOnProfitValue">
          {({
            handleChange,
            handleBlur,
            state: {
              value,
              meta: { errors },
            },
            name,
          }) => (
            <NumberInput
              allowNegative={false}
              data-test-id="stop-on-profit-input"
              decimalScale={2}
              disabled={isAutoBetting}
              error={errors[0]?.message}
              fixedDecimalScale
              id={name}
              label={t('configureAutoBetModal.stopOnProfitLabel')}
              labelProps={{
                'data-test-id': 'stop-on-profit-input-label',
              }}
              max={1_000_000_000}
              name={name}
              onBlur={handleBlur}
              onChange={(newValue) => handleChange(Number(newValue) || 0)}
              placeholder="0.00"
              suffix={` ${currency}`}
              value={value}
            />
          )}
        </Field>

        <Field name="stopOnLossValue">
          {({
            handleChange,
            handleBlur,
            state: {
              value,
              meta: { errors },
            },
            name,
          }) => (
            <NumberInput
              allowNegative={false}
              data-test-id="stop-on-loss-input"
              decimalScale={2}
              disabled={isAutoBetting}
              error={errors[0]?.message}
              fixedDecimalScale
              id={name}
              label={t('configureAutoBetModal.stopOnLossLabel')}
              labelProps={{
                'data-test-id': 'stop-on-loss-input-label',
              }}
              max={1_000_000_000}
              name={name}
              onBlur={handleBlur}
              onChange={(newValue) => handleChange(Number(newValue) || 0)}
              placeholder="0.00"
              suffix={` ${currency}`}
              value={value}
            />
          )}
        </Field>

        <Stack data-test-id="configure-auto-bet-modal-actions" gap="md" mt={12}>
          <Button data-test-id="apply-button" fullWidth size="lg" type="submit">
            {t('configureAutoBetModal.applyButtonLabel')}
          </Button>

          <Button
            data-test-id="reset-all-button"
            fullWidth
            onClick={handleResetAll}
            size="lg"
            type="button"
            variant="subtle"
          >
            {t('configureAutoBetModal.resetAllButtonLabel')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
